import { useState, useCallback, useEffect } from "react";

interface PublicHeaderProps {
  activePage?: "home" | "about" | "services" | "works" | "recruit" | "contact" | "news" | "privacy-policy";
}

export const PublicHeader = ({ activePage }: PublicHeaderProps) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isLinePopupOpen, setIsLinePopupOpen] = useState(false);

  // モバイルメニューが開いているときはスクロールを無効化
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isMobileMenuOpen]);

  const toggleMobileMenu = useCallback(() => {
    setIsMobileMenuOpen((prev) => !prev);
  }, []);

  const closeMobileMenu = useCallback(() => {
    setIsMobileMenuOpen(false);
  }, []);

  const LINE_URL = "https://line.me/R/ti/p/@703jwzlx?from=page&accountId=703jwzlx";

  const isMobileDevice = useCallback(() => {
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
      navigator.userAgent
    );
  }, []);

  const handleLineClick = useCallback(() => {
    // モバイル端末は直接LINEに遷移、PCはポップアップ表示
    if (isMobileDevice()) {
      window.open(LINE_URL, "_blank", "noopener,noreferrer");
    } else {
      setIsLinePopupOpen(true);
    }
  }, [isMobileDevice]);

  const closeLinePopup = useCallback(() => {
    setIsLinePopupOpen(false);
  }, []);

  return (
    <header className="public-header" role="banner">
      <div className="header-container">
        <div className="logo-section">
          <a href="/" className="logo-link" aria-label="都建設株式会社 ホーム">
            <img
              src="/static/miyako-logo-small.png"
              srcSet="/static/miyako-logo-small.png 1x, /static/miyako-logo-2x.png 2x"
              alt="都建設株式会社"
              className="miyako-logo-image"
            />
            <span className="logo-text">都建設株式会社</span>
          </a>
        </div>

        <div className="header-right">
          <nav className="main-nav" role="navigation" aria-label="メインナビゲーション">
            <a href="/" className={`nav-link ${activePage === "home" ? "active" : ""}`}>
              <span className="nav-ja">ホーム</span>
              <span className="nav-en" aria-hidden="true">HOME</span>
            </a>
            <a href="/about" className={`nav-link ${activePage === "about" ? "active" : ""}`}>
              <span className="nav-ja">会社概要</span>
              <span className="nav-en" aria-hidden="true">ABOUT US</span>
            </a>
            <a href="/services" className={`nav-link ${activePage === "services" ? "active" : ""}`}>
              <span className="nav-ja">業務内容</span>
              <span className="nav-en" aria-hidden="true">SERVICE</span>
            </a>
            <a href="/works" className={`nav-link ${activePage === "works" ? "active" : ""}`}>
              <span className="nav-ja">施工実績</span>
              <span className="nav-en" aria-hidden="true">WORKS</span>
            </a>
            <a href="/recruit" className={`nav-link ${activePage === "recruit" ? "active" : ""}`}>
              <span className="nav-ja">採用情報</span>
              <span className="nav-en" aria-hidden="true">RECRUIT</span>
            </a>
            <a href="/contact" className={`nav-link ${activePage === "contact" ? "active" : ""}`}>
              <span className="nav-ja">お問い合わせ</span>
              <span className="nav-en" aria-hidden="true">CONTACT</span>
            </a>
          </nav>

          <div className="header-actions">
            <button
              className="line-icon"
              aria-label="公式LINEで問い合わせ"
              data-track="line"
              onClick={handleLineClick}
              type="button"
            >
              <svg viewBox="0 0 48 48" width="44" height="44" aria-hidden="true">
                <rect width="48" height="48" rx="10" fill="#06C755" />
                <path
                  d="M24 8c-8.84 0-16 5.82-16 12.99 0 6.43 5.7 11.82 13.4 12.84.52.11 1.23.35 1.41.79.16.41.1 1.04.05 1.45l-.23 1.37c-.07.41-.32 1.6 1.4.87 1.72-.73 9.27-5.46 12.65-9.35C39 26.44 40 23.96 40 20.99 40 13.82 32.84 8 24 8z"
                  fill="white"
                />
                <text
                  x="24"
                  y="23"
                  fill="#06C755"
                  fontSize="9"
                  fontWeight="900"
                  fontFamily="Arial Black, Arial, sans-serif"
                  textAnchor="middle"
                  dominantBaseline="middle"
                  letterSpacing="-0.5"
                >
                  LINE
                </text>
              </svg>
            </button>
            <a href="/contact" className="contact-button">
              お問い合わせ
            </a>
          </div>

          {/* モバイルメニュートグルボタン */}
          <button
            className="mobile-menu-toggle"
            onClick={toggleMobileMenu}
            aria-label={isMobileMenuOpen ? "メニューを閉じる" : "メニューを開く"}
            aria-expanded={isMobileMenuOpen}
            aria-controls="mobile-nav"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              {isMobileMenuOpen ? (
                <>
                  <line x1="6" y1="6" x2="18" y2="18" />
                  <line x1="6" y1="18" x2="18" y2="6" />
                </>
              ) : (
                <>
                  <line x1="3" y1="6" x2="21" y2="6" />
                  <line x1="3" y1="12" x2="21" y2="12" />
                  <line x1="3" y1="18" x2="21" y2="18" />
                </>
              )}
            </svg>
          </button>
        </div>
      </div>

      {/* モバイルナビゲーション */}
      <nav
        id="mobile-nav"
        className={`mobile-nav ${isMobileMenuOpen ? "open" : ""}`}
        role="navigation"
        aria-label="モバイルナビゲーション"
      >
        <a href="/" className="mobile-nav-link" onClick={closeMobileMenu}>
          ホーム
        </a>
        <a href="/about" className="mobile-nav-link" onClick={closeMobileMenu}>
          会社概要
        </a>
        <a href="/services" className="mobile-nav-link" onClick={closeMobileMenu}>
          業務内容
        </a>
        <a href="/works" className="mobile-nav-link" onClick={closeMobileMenu}>
          施工実績
        </a>
        <a href="/recruit" className="mobile-nav-link" onClick={closeMobileMenu}>
          採用情報
        </a>
        <a href="/contact" className="mobile-nav-link contact" onClick={closeMobileMenu}>
          お問い合わせ
        </a>
        <button
          type="button"
          className="mobile-nav-link line"
          data-track="line"
          onClick={() => {
            closeMobileMenu();
            handleLineClick();
          }}
        >
          <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
            <path
              d="M12 2C6.48 2 2 5.82 2 10.49c0 4.21 3.74 7.74 8.79 8.41.34.07.8.23.92.52.1.27.07.68.03.95l-.15.89c-.05.27-.21 1.05.92.57 1.13-.48 6.09-3.58 8.3-6.14C22.21 13.91 22 12.29 22 10.49 22 5.82 17.52 2 12 2z"
              fill="currentColor"
            />
          </svg>
          公式LINEで問い合わせ
        </button>
      </nav>
      {isMobileMenuOpen && (
        <div className="mobile-nav-overlay" onClick={closeMobileMenu} aria-hidden="true" />
      )}

      {/* LINE公式アカウント ポップアップ */}
      {isLinePopupOpen && (
        <>
          <div className="line-popup-overlay" onClick={closeLinePopup} aria-hidden="true" />
          <div className="line-popup" role="dialog" aria-modal="true" aria-labelledby="line-popup-title">
            <button
              className="line-popup-close"
              onClick={closeLinePopup}
              aria-label="閉じる"
              type="button"
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                <line x1="6" y1="6" x2="18" y2="18" />
                <line x1="6" y1="18" x2="18" y2="6" />
              </svg>
            </button>
            <div className="line-popup-content">
              <h3 id="line-popup-title" className="line-popup-title">LINE公式アカウント</h3>
              <p className="line-popup-description">
                お気軽にお問い合わせください。<br />
                QRコードを読み取るか、下のボタンから友だち追加できます。
              </p>
              <div className="line-popup-qr">
                <img
                  src="https://qr-official.line.me/gs/M_703jwzlx_GW.png"
                  alt="LINE公式アカウント QRコード"
                  width="180"
                  height="180"
                />
              </div>
              <a
                href="https://line.me/R/ti/p/@703jwzlx?from=page&accountId=703jwzlx"
                className="line-popup-button"
                target="_blank"
                rel="noopener noreferrer"
              >
                <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
                  <path
                    d="M12 2C6.48 2 2 5.82 2 10.49c0 4.21 3.74 7.74 8.79 8.41.34.07.8.23.92.52.1.27.07.68.03.95l-.15.89c-.05.27-.21 1.05.92.57 1.13-.48 6.09-3.58 8.3-6.14C22.21 13.91 22 12.29 22 10.49 22 5.82 17.52 2 12 2z"
                    fill="white"
                  />
                </svg>
                友だち追加
              </a>
            </div>
          </div>
        </>
      )}
    </header>
  );
};

export default PublicHeader;
